import { defineStore } from 'pinia'
import { nextTick, ref, watch } from 'vue'

import { apiService } from '@/services/apiService'
import CONFIG from '@/services/configService'
import { connectivityService } from '@/services/connectivityService'
import { parseFIT } from '@/services/fitService'
import { formatByteSize } from '@/services/formattingService'
import { parseGPXFromArrayBuffer } from '@/services/gpxService'
import { generateId } from '@/services/idService'
import { buildLineStringFromSegments } from '@/services/lineStringService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { loadLibraryToursFiltered } from '@/services/offlineContentProvider'
import { throwIfOfflineReadOnly } from '@/services/offlineModificationGuardService'
import { settingsService } from '@/services/settingsService'
import { importSuccessMessage, syncToasts } from '@/services/syncCopy'
import { syncDialogVisible } from '@/services/syncUiState'
import { showError, showSuccess } from '@/services/toastService'
import { isKnownTrackTypeId } from '@/services/tourTypeService'
import { TrackColorService } from '@/services/trackColorService'
import {
  calculateTrackDistance,
  getTrackElevationGain,
} from '@/services/trackMetricsCalculationService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import { EventType } from '@/types/event'
import type { Event, EventChange } from '@/types/event'
import type { RecordingDraft, RecordingPoint } from '@/types/recording'
import type { Segment, Tour, TourListItem, Track, Waypoint, RoutePoint } from '@/types/tour'
import {
  ensureSegmentClientIds,
  ensureTrackSegmentClientIds,
} from '@/utils/ensureTrackSegmentClientIds'
import { hydrateApiSegments } from '@/utils/hydrateApiSegments'
import { ensureImportedRoutePoints } from '@/utils/importRoutePointFallback'
import { requireItemEditable } from '@/utils/permissionDialog'
import { refreshTourTrackStats, refreshTrackMetrics } from '@/utils/refreshTrackMetrics'
import { findNearestTrackClientId } from '@/utils/routePointGrouping'
import {
  getRoutePointInsertOrderPlan,
  getRoutePointMultiDeletePlan,
  getRoutePointMultiInsertOrderPlan,
  getSparseTrackReorderPlan,
  ROUTE_POINT_ORDER_STEP,
  getSparseTrackAppendOrder,
  getSparseTrackSeedOrders,
} from '@/utils/routePointOrder'
import { sortSegmentOrderShiftsForEmission } from '@/utils/segmentOrder'
import { requireTrackClientId } from '@/utils/trackRoutePoints'
import { simplifyTrackPoints } from '@/utils/trackSimplification'
import { truncateToMaxLength } from '@/utils/validation'

import { useEventStore } from './eventStore'
import { useOfflineStore } from './offlineStore'
import { useUiStore } from './uiStore'
import { useWorkspaceStore } from './workspaceStore'

import type { TrackRouteDraftCommitInput } from './workspaceStore'

function getPersistedTourType(
  explicitType: string | null | undefined,
  tracks: Array<Pick<Track, 'type'>>,
): string | undefined {
  if (isKnownTrackTypeId(explicitType)) {
    return explicitType
  }

  return tracks.map((track) => track.type).find(isKnownTrackTypeId) ?? undefined
}

const clonePlain = <T>(value: T): T => JSON.parse(JSON.stringify(value))

const routePointLocation = (routePoint: Pick<RoutePoint, 'lat' | 'lon'>) =>
  `SRID=4326;POINT(${routePoint.lon} ${routePoint.lat})`

const samePlainValue = (left: unknown, right: unknown) =>
  JSON.stringify(left ?? null) === JSON.stringify(right ?? null)

const sameSegment = (left: Segment, right: Segment) =>
  samePlainValue(clonePlain(left), clonePlain(right))

const replaceTrackRoutePoints = (
  tour: Tour,
  trackClientId: string,
  finalRoutePoints: RoutePoint[],
) => {
  const firstTrackRoutePointIndex = tour.routePoints.findIndex(
    (routePoint) => (routePoint.trackClientId ?? null) === trackClientId,
  )
  const remainingRoutePoints = tour.routePoints.filter(
    (routePoint) => (routePoint.trackClientId ?? null) !== trackClientId,
  )
  const insertIndex =
    firstTrackRoutePointIndex >= 0
      ? Math.min(firstTrackRoutePointIndex, remainingRoutePoints.length)
      : remainingRoutePoints.length

  remainingRoutePoints.splice(insertIndex, 0, ...finalRoutePoints)
  tour.routePoints = remainingRoutePoints
}

type RoutePointOrderEmission = {
  clientId: string
  previousOrder: number
  nextOrder: number
  finalChanges: EventChange[]
}

const emitRoutePointOrderChanges = async (
  queueRoutePointUpdate: (clientId: string, changes: EventChange[]) => Promise<unknown>,
  updates: RoutePointOrderEmission[],
  currentOrders: number[],
  nextOrders: number[],
) => {
  if (updates.length === 0) return

  if (updates.length > 1) {
    const temporaryOrderBase =
      Math.max(-1, ...currentOrders, ...nextOrders) + ROUTE_POINT_ORDER_STEP

    for (const update of updates) {
      await queueRoutePointUpdate(update.clientId, [
        { field: 'order', value: update.previousOrder + temporaryOrderBase },
      ])
    }
  }

  for (const update of updates) {
    await queueRoutePointUpdate(update.clientId, update.finalChanges)
  }
}

export const useLibraryStore = defineStore('library', () => {
  const savedSelectedTourIds = settingsService.getSetting<string[]>(
    UI_SETTING_KEYS.librarySelectedTourIds,
  )
  const restoredSelectedTourIds = Array.isArray(savedSelectedTourIds)
    ? savedSelectedTourIds.filter((tourId): tourId is string => typeof tourId === 'string')
    : []

  const savedUploadedBatchIds = settingsService.getSetting<string[]>(
    UI_SETTING_KEYS.libraryUploadedBatchIds,
  )
  const restoredUploadedBatchIds = Array.isArray(savedUploadedBatchIds)
    ? savedUploadedBatchIds.filter((id): id is string => typeof id === 'string')
    : []

  const restoredShowOnlyUploadedBatch =
    restoredUploadedBatchIds.length > 0 &&
    settingsService.getSetting<boolean>(UI_SETTING_KEYS.libraryShowOnlyUploadedBatch) === true

  const tours = ref<TourListItem[]>([])
  const totalCount = ref(0)
  const searchQuery = ref('')
  const showNoWorkspace = ref(false)
  const dateFrom = ref<string | null>(null)
  const dateTo = ref<string | null>(null)
  const isLoading = ref(false)
  const hasLoaded = ref(false)
  const hoveredTourId = ref<string | null>(null)
  const focusedTourId = ref<string | null>(null)
  const scrollTargetTourId = ref<string | null>(null)
  const isSelectionMode = ref(false)
  const recentlyUploadedIds = ref<Set<string>>(new Set())
  const uploadedBatchIds = ref<Set<string>>(new Set(restoredUploadedBatchIds))
  const showOnlyUploadedBatch = ref(restoredShowOnlyUploadedBatch)
  // IDs of newly drawn (not uploaded) tours that should bypass list + map filters
  // until the user explicitly changes a filter. Accumulates across multiple draws.
  // Set by createEmptyTour, cleared by any filter change or by clearPinnedTours().
  const pinnedTourIds = ref<Set<string>>(new Set())
  const selectedTourIds = ref<Set<string>>(new Set(restoredSelectedTourIds))
  const locallyModifiedTourIds = ref<Set<string>>(new Set())
  const uploadedTourDrafts = new Map<string, Tour>()
  let listLoadGeneration = 0
  let detailLoadGeneration = 0

  watch(selectedTourIds, (tourIds) => {
    try {
      settingsService.saveSetting(UI_SETTING_KEYS.librarySelectedTourIds, Array.from(tourIds))
    } catch (error) {
      showError(
        `Failed to save selected tours: ${error instanceof Error ? error.message : String(error)}`,
      )
    }
  })

  watch(uploadedBatchIds, (ids) => {
    try {
      settingsService.saveSetting(UI_SETTING_KEYS.libraryUploadedBatchIds, Array.from(ids))
    } catch (error) {
      logService.error(LOG_CATEGORIES.UI, 'Failed to save uploaded batch IDs', { error })
    }
  })

  watch(showOnlyUploadedBatch, (val) => {
    try {
      settingsService.saveSetting(UI_SETTING_KEYS.libraryShowOnlyUploadedBatch, val)
    } catch (error) {
      logService.error(LOG_CATEGORIES.UI, 'Failed to save showOnlyUploadedBatch', { error })
    }
  })

  // Clear pins whenever store-side filter state changes (component-local filter
  // changes are handled by the component calling clearPinnedTours directly).
  watch([searchQuery, dateFrom, dateTo, showOnlyUploadedBatch], () => {
    if (pinnedTourIds.value.size > 0) pinnedTourIds.value = new Set()
  })

  const loadTours = async () => {
    const loadGeneration = ++listLoadGeneration
    isLoading.value = true
    try {
      const nextTours = await loadLibraryToursFiltered({
        noWorkspace: showNoWorkspace.value || undefined,
      })

      if (loadGeneration === listLoadGeneration) {
        tours.value = nextTours
        totalCount.value = nextTours.length
      }
    } catch (error) {
      if (loadGeneration === listLoadGeneration) {
        logService.error(LOG_CATEGORIES.API, 'Failed to load tours for library view', { error })
      }
    } finally {
      if (loadGeneration === listLoadGeneration) {
        isLoading.value = false
        hasLoaded.value = true
      }
    }
  }

  const setSearch = (query: string) => {
    searchQuery.value = query
  }

  const toggleNoWorkspace = () => {
    void loadTours()
  }

  // Client-side filter IDs (type / collection / distance). null = no filter active.
  const clientSideFilteredIds = ref<Set<string> | null>(null)

  const setClientFilter = (ids: Set<string> | null) => {
    clientSideFilteredIds.value = ids
  }

  const setDateRange = (from: string | null, to: string | null) => {
    dateFrom.value = from || null
    dateTo.value = to || null
  }

  const resetFilters = () => {
    searchQuery.value = ''
    showNoWorkspace.value = false
    dateFrom.value = null
    dateTo.value = null
    void loadTours()
  }

  const prependTours = (newTours: TourListItem[]) => {
    tours.value = [...newTours, ...tours.value]
    totalCount.value += newTours.length
    const newIds = newTours.map((t) => t.clientId)
    selectedTourIds.value = new Set([...selectedTourIds.value, ...newIds])
    recentlyUploadedIds.value = new Set(newIds)
    uploadedBatchIds.value = new Set(newIds)
    showOnlyUploadedBatch.value = true
  }

  const clearRecentlyUploaded = () => {
    recentlyUploadedIds.value = new Set()
  }

  const clearUploadedBatchFilter = () => {
    showOnlyUploadedBatch.value = false
  }

  const clearPinnedTours = () => {
    pinnedTourIds.value = new Set()
  }

  const setSelectedTourIds = (tourIds: Iterable<string>) => {
    selectedTourIds.value = new Set(tourIds)
  }

  const toggleSelectedTour = (tourId: string) => {
    const next = new Set(selectedTourIds.value)
    if (next.has(tourId)) {
      next.delete(tourId)
    } else {
      next.add(tourId)
    }
    selectedTourIds.value = next
    if (next.size === 0 && isSelectionMode.value) {
      isSelectionMode.value = false
    }
  }

  const ensureTourSelected = (tourId: string) => {
    if (selectedTourIds.value.has(tourId)) {
      return
    }
    const next = new Set(selectedTourIds.value)
    next.add(tourId)
    selectedTourIds.value = next
  }

  const clearSelectedTours = () => {
    selectedTourIds.value = new Set()
  }

  const removeTours = (tourIds: string[]) => {
    const idSet = new Set(tourIds)
    tours.value = tours.value.filter((t) => !idSet.has(t.clientId))
    if (detailTour.value && idSet.has(detailTour.value.clientId)) {
      detailTour.value = null
    }
    totalCount.value = Math.max(0, totalCount.value - tourIds.length)
    selectedTourIds.value = new Set(
      Array.from(selectedTourIds.value).filter((tourId) => !idSet.has(tourId)),
    )
    locallyModifiedTourIds.value = new Set(
      Array.from(locallyModifiedTourIds.value).filter((tourId) => !idSet.has(tourId)),
    )
    if ([...idSet].some((id) => uploadedBatchIds.value.has(id))) {
      uploadedBatchIds.value = new Set(
        Array.from(uploadedBatchIds.value).filter((id) => !idSet.has(id)),
      )
      if (uploadedBatchIds.value.size === 0) showOnlyUploadedBatch.value = false
    }
    if ([...idSet].some((id) => pinnedTourIds.value.has(id))) {
      pinnedTourIds.value = new Set(Array.from(pinnedTourIds.value).filter((id) => !idSet.has(id)))
    }
    if (detailTour.value && idSet.has(detailTour.value.clientId)) {
      closeDetail()
    }
  }

  const buildTrackLineString = (segments: Segment[]): string | undefined => {
    const coords: string[] = []

    for (const segment of segments) {
      for (const point of segment.points) {
        coords.push(`${point.lon} ${point.lat}`)
      }
    }

    return coords.length >= 2 ? `LINESTRING(${coords.join(', ')})` : undefined
  }

  const pendingUpdatedAtFetches = new Set<string>()

  const markTourLocallyModified = (tourId: string) => {
    const next = new Set(locallyModifiedTourIds.value)
    next.add(tourId)
    locallyModifiedTourIds.value = next

    // Optimistically set updatedAt so sort re-runs immediately; scheduleFetchUpdatedAt
    // will overwrite with the confirmed server value once the event queue drains.
    const item = tours.value.find((t) => t.clientId === tourId)
    if (item) item.updatedAt = new Date().toISOString()

    scheduleFetchUpdatedAt(tourId)
  }

  /**
   * After any mutation event for a tour has synced, fetch the server-assigned
   * updatedAt and patch it onto the in-memory TourListItem so "Recently modified"
   * sort reflects the change without requiring a full loadTours() reload.
   * De-duplicated: multiple rapid mutations to the same tour share one watcher.
   */
  function scheduleFetchUpdatedAt(tourId: string) {
    if (pendingUpdatedAtFetches.has(tourId)) return
    pendingUpdatedAtFetches.add(tourId)

    const eventStore = useEventStore()

    const tryPatch = async () => {
      pendingUpdatedAtFetches.delete(tourId)
      const item = tours.value.find((t) => t.clientId === tourId)
      if (!item) return
      try {
        const detail = await apiService.getTourDetail(tourId)
        const current = tours.value.find((t) => t.clientId === tourId)
        if (current && detail.updatedAt) current.updatedAt = detail.updatedAt
      } catch (error) {
        logService.warn(LOG_CATEGORIES.API, 'Failed to fetch updatedAt for tour', {
          tourId,
          error,
        })
      }
    }

    if (eventStore.unsyncedEvents.length === 0) {
      void tryPatch()
      return
    }

    const unwatch = watch(
      () => eventStore.unsyncedEvents.length,
      (len) => {
        if (len === 0) {
          unwatch()
          void tryPatch()
        }
      },
    )
  }

  /**
   * After a new tour's TOUR_CREATED event has synced, fetch its server-assigned
   * createdAt and patch it onto the in-memory TourListItem so the library list
   * can show Date Added without requiring a full loadTours() reload.
   */
  const scheduleFetchCreatedAt = (tourId: string) => {
    const tryPatch = async () => {
      const listItem = tours.value.find((t) => t.clientId === tourId)
      if (!listItem || listItem.createdAt) return
      try {
        const detail = await apiService.getTourDetail(tourId)
        const item = tours.value.find((t) => t.clientId === tourId)
        if (item && detail.createdAt) {
          item.createdAt = detail.createdAt
        }
      } catch (error) {
        logService.warn(LOG_CATEGORIES.API, 'Failed to fetch createdAt for new tour', {
          tourId,
          error,
        })
      }
    }

    const eventStore = useEventStore()
    const hasTourCreatedEvent = () =>
      eventStore.unsyncedEvents.some(
        (e) =>
          e.type === EventType.TOUR_CREATED &&
          (e.payload as { clientId?: string }).clientId === tourId,
      )

    if (!hasTourCreatedEvent()) {
      void tryPatch()
      return
    }

    const unwatch = watch(
      () => eventStore.unsyncedEvents,
      () => {
        if (!hasTourCreatedEvent()) {
          unwatch()
          void tryPatch()
        }
      },
    )
  }

  const syncTourListMetrics = (tourId: string, tour: Tour) => {
    const tourListItem = tours.value.find((candidate) => candidate.clientId === tourId)
    if (!tourListItem) return

    tourListItem.distance = tour.distance
    tourListItem.elevationGain = tour.elevationGain ?? null
  }

  /**
   * Parse and upload GPX/FIT files directly in library mode (no workspace created).
   * Parsed tours are prepended to the tour list for immediate display and
   * TOUR_CREATED events are generated so tours persist server-side.
   */
  const uploadTourFiles = async (
    files: File[],
    options: { appendToUploadedBatch?: boolean } = {},
  ): Promise<number> => {
    const validFiles = files.filter((f) => {
      const name = f.name.toLowerCase()
      if (!name.endsWith('.gpx') && !name.endsWith('.fit')) return false
      if (f.size > CONFIG.UPLOAD_MAX_FILE_SIZE) {
        showError(
          `File "${f.name}" exceeds the maximum size of ${formatByteSize(CONFIG.UPLOAD_MAX_FILE_SIZE)}.`,
        )
        return false
      }
      return true
    })
    if (validFiles.length === 0) return 0

    // Clear filter state without triggering loadTours() - we'll show only the uploaded batch
    searchQuery.value = ''
    showNoWorkspace.value = false
    dateFrom.value = null
    dateTo.value = null

    // Open the All Activities panel immediately so the user can watch files render
    window.dispatchEvent(new CustomEvent('open-all-activities'))

    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()

    const initialTourCount = tours.value.length
    let addedCount = 0
    const allUploadedIds: string[] = []
    const existingUploadedBatchIds = options.appendToUploadedBatch
      ? Array.from(uploadedBatchIds.value)
      : []

    const result = await eventStore.runInSyncBatch(async () => {
      for (let i = 0; i < validFiles.length; i++) {
        const file = validFiles[i]
        const fileName = file.name.toLowerCase()
        try {
          let parsedTour
          const content = await file.arrayBuffer()

          if (fileName.endsWith('.gpx')) {
            parsedTour = await parseGPXFromArrayBuffer(content, file.name, file)
          } else if (fileName.endsWith('.fit')) {
            parsedTour = await parseFIT(content, file.name, file)
          }

          if (parsedTour) {
            const tourId = generateId()

            // Assign colors to tracks for persistence
            const normalizedTracks = ensureTrackSegmentClientIds(parsedTour.tracks).map(
              (track, trackIndex) => ({
                ...track,
                clientId: track.clientId || generateId(),
                color: TrackColorService.getColorByIndex(
                  initialTourCount + addedCount + trackIndex,
                ),
              }),
            )
            parsedTour.tracks = normalizedTracks
            parsedTour.routePoints = ensureImportedRoutePoints(
              parsedTour.routePoints || [],
              normalizedTracks,
              generateId,
            ).map((routePoint, index) => ({
              ...routePoint,
              clientId: routePoint.clientId || generateId(),
              order: routePoint.order ?? index,
              trackClientId:
                routePoint.trackClientId ??
                findNearestTrackClientId(
                  normalizedTracks,
                  routePoint.lat as number,
                  routePoint.lon as number,
                ),
            }))

            // Derive start/end dates from track timestamps
            let earliestTime: number | null = null
            let latestTime: number | null = null
            for (const track of parsedTour.tracks) {
              if (track.startTime != null) {
                if (earliestTime === null || track.startTime < earliestTime)
                  earliestTime = track.startTime
              }
              if (track.endTime != null) {
                if (latestTime === null || track.endTime > latestTime) latestTime = track.endTime
              }
            }

            // Sum elevation gain using the shared fallback helper so BRouter-drawn
            // tracks match the rest of the UI even before a TrackMetric row exists.
            let totalElevationGain: number | null = null
            for (const track of parsedTour.tracks) {
              const gain = getTrackElevationGain(track)
              if (gain != null) totalElevationGain = (totalElevationGain ?? 0) + gain
            }

            // Build TourListItem for immediate UI display
            const tourItem: TourListItem = {
              clientId: tourId,
              name: parsedTour.name,
              description: parsedTour.description ?? null,
              type: getPersistedTourType(
                parsedTour.type as string | undefined,
                parsedTour.tracks as Array<Pick<Track, 'type'>>,
              ),
              distance: parsedTour.distance,
              elevationGain: totalElevationGain,
              startDate: earliestTime ? new Date(earliestTime).toISOString() : null,
              endDate: latestTime ? new Date(latestTime).toISOString() : null,
              createdAt: new Date().toISOString(),
              workspaces: [],
              tracks: parsedTour.tracks.map((track) => {
                // Generate LINESTRING from segments for map display
                const coords: string[] = []
                for (const segment of track.segments) {
                  for (const point of segment.points) {
                    coords.push(`${point.lon} ${point.lat}`)
                  }
                }
                return {
                  clientId: track.clientId,
                  pointsLineReduced500m:
                    coords.length >= 2 ? `LINESTRING(${coords.join(', ')})` : undefined,
                  color: track.color,
                }
              }),
            }

            // Add to UI immediately so the user sees each file as it's processed
            allUploadedIds.push(tourId)
            tours.value = [tourItem, ...tours.value]
            uploadedTourDrafts.set(tourId, {
              ...parsedTour,
              clientId: tourId,
              type: getPersistedTourType(
                parsedTour.type as string | undefined,
                parsedTour.tracks as Array<Pick<Track, 'type'>>,
              ),
              visible: true,
              active: false,
              order: 0,
              tracks: parsedTour.tracks.map((track) => ({
                ...track,
                clientId: track.clientId,
                visible: track.visible !== undefined ? track.visible : true,
              })),
              routePoints: parsedTour.routePoints.map((routePoint) => ({
                ...routePoint,
                clientId: routePoint.clientId || generateId(),
                trackClientId: requireTrackClientId(
                  routePoint.trackClientId,
                  'Imported route point',
                ),
              })),
            })
            totalCount.value += 1
            selectedTourIds.value = new Set([...selectedTourIds.value, tourId])
            const visibleUploadedIds = [...existingUploadedBatchIds, ...allUploadedIds]
            recentlyUploadedIds.value = new Set(visibleUploadedIds)
            uploadedBatchIds.value = new Set(visibleUploadedIds)
            showOnlyUploadedBatch.value = true
            addedCount++

            // Build TOUR_CREATED event payload with full tour data
            const {
              _originalFile: eventFile,
              eventFileName,
              eventFileOriginalName,
              eventFileMimeType,
              eventFileCompressed,
              ...tourPayload
            } = parsedTour

            const eventFileMetadata = {
              name: eventFileName || eventFile?.name || file.name,
              originalName: eventFileOriginalName || file.name,
              mimeType: eventFileMimeType || eventFile?.type || file.type,
              compressed: eventFileCompressed ?? false,
            }

            const originalSourceFilename =
              tourPayload.sourceFilename || eventFileMetadata.originalName
            const sourceFilename =
              truncateToMaxLength(originalSourceFilename, 4096) ??
              originalSourceFilename.slice(0, 4096)

            const eventPayload: Record<string, unknown> = {
              workspaceClientId: null,
              ...tourPayload,
              clientId: tourId,
              order: 0,
              sourceFilename,
              eventFileName: eventFileMetadata.name,
              eventFileOriginalName: eventFileMetadata.originalName,
              eventFileMimeType: eventFileMetadata.mimeType,
              eventFileCompressed: eventFileMetadata.compressed,
            }

            if (eventFile) {
              eventPayload.eventFile = eventFile
            }

            workspaceStore._createEventPushed = true
            await eventStore.addEvent({
              type: EventType.TOUR_CREATED,
              payload: eventPayload,
            } as Omit<Event, 'clientId' | 'timestamp'>)
            scheduleFetchCreatedAt(tourId)
          }
        } catch (error) {
          // Quota check cancelled: the file was NOT saved — say so instead of a parse error
          // (docs/concepts/sync-feedback.md §10.2).
          if (error instanceof Error && error.message === 'QUOTA_CHECK_CANCELLED') {
            logService.warn(LOG_CATEGORIES.FILE, 'Import skipped: quota check cancelled', {
              fileName: file.name,
            })
            showError(`"${file.name}": ${syncToasts.itemNotSaved}`)
            continue
          }
          logService.error(LOG_CATEGORIES.FILE, `Failed to parse file: ${file.name}`, { error })
          showError(
            `Failed to parse "${file.name}": ${error instanceof Error ? error.message : String(error)}`,
          )
        }
      }

      return addedCount
    })

    if (result > 0) {
      window.dispatchEvent(
        new CustomEvent('library-tours-uploaded', {
          detail: { tourIds: [...existingUploadedBatchIds, ...allUploadedIds] },
        }),
      )
      // Confirm the import — honest about the background sync (sync-feedback.md §4.3).
      // Skipped while the sync dialog is open: it already lists the items with live status.
      if (!syncDialogVisible.value) {
        showSuccess(importSuccessMessage(result, connectivityService.isEffectivelyOnline.value))
      }
    }

    return result
  }

  const setHoveredTour = (tourId: string) => {
    hoveredTourId.value = tourId
  }

  const clearHoveredTour = () => {
    hoveredTourId.value = null
  }

  const setFocusedTour = (tourId: string) => {
    if (focusedTourId.value === tourId) {
      focusedTourId.value = null
      void nextTick(() => {
        focusedTourId.value = tourId
      })
      return
    }
    focusedTourId.value = tourId
  }

  const clearFocusedTour = () => {
    focusedTourId.value = null
  }

  // ── Tour detail panel (library context) ─────────────────────────────────────

  const detailTour = ref<Tour | null>(null)
  const isLoadingDetail = ref(false)

  const selectTourForDetail = (tour: TourListItem, options: { focusMap?: boolean } = {}) => {
    // Show panel instantly with lightweight data from TourListItem
    const uploadedDraft = uploadedTourDrafts.get(tour.clientId)
    detailTour.value = uploadedDraft ?? {
      clientId: tour.clientId,
      name: tour.name,
      description: tour.description ?? undefined,
      type: tour.type ?? undefined,
      distance: tour.distance ?? null,
      time_moving: null,
      time_elapsed: null,
      visible: true,
      active: false,
      tracks: tour.tracks.map((t) => ({
        clientId: t.clientId ?? '',
        name: '',
        segments: [],
        color: t.color,
        visible: true,
        pointsLineReduced5m: t.pointsLineReduced500m,
      })),
      waypoints: [],
      routePoints: [],
    }
    if (options.focusMap !== false) {
      setFocusedTour(tour.clientId)
    }

    const uiStore = useUiStore()
    uiStore.showTourDetailPanel()

    // Fetch full tour data in the background
    void loadFullTourDetail(tour.clientId)
  }

  const loadFullTourDetail = async (tourId: string) => {
    const loadGeneration = ++detailLoadGeneration
    isLoadingDetail.value = true
    try {
      const fullTour = await apiService.getTourDetail(tourId)

      if (loadGeneration !== detailLoadGeneration) return

      if (!fullTour) {
        throw new Error('Tour detail is empty')
      }

      // Only update if this tour is still the one being viewed
      if (detailTour.value?.clientId === tourId) {
        detailTour.value = {
          ...fullTour,
          visible: fullTour.visible !== undefined ? fullTour.visible : true,
          active: fullTour.active !== undefined ? fullTour.active : false,
          tracks: fullTour.tracks.map((track) => ({
            ...track,
            visible: track.visible !== undefined ? track.visible : true,
            segments: hydrateApiSegments(track.segments),
          })),
          routePoints: (fullTour.routePoints ?? []).map((rp, index) => ({
            ...rp,
            clientId: rp.clientId || generateId(),
            order: rp.order ?? index,
            trackClientId: requireTrackClientId(rp.trackClientId, 'Loaded route point'),
            _previousState: undefined,
          })),
        }
        uploadedTourDrafts.delete(tourId)
      }
    } catch (error) {
      if (loadGeneration !== detailLoadGeneration) return
      const offlineStore = useOfflineStore()
      const offlineTour = await offlineStore.findOfflineTourDetail(tourId)
      if (loadGeneration !== detailLoadGeneration) return
      if (offlineTour && detailTour.value?.clientId === tourId) {
        detailTour.value = offlineTour
      } else {
        logService.error(LOG_CATEGORIES.API, 'Failed to load full tour detail', { error })
      }
    } finally {
      if (loadGeneration === detailLoadGeneration) {
        isLoadingDetail.value = false
      }
    }
  }

  const closeDetail = () => {
    detailLoadGeneration += 1
    detailTour.value = null
    isLoadingDetail.value = false
    const uiStore = useUiStore()
    uiStore.hideTourDetailPanel()
  }

  const updateTour = async (
    tourId: string,
    updates: { name?: string; description?: string; type?: string; routingProfile?: string | null },
  ) => {
    throwIfOfflineReadOnly('edit tour')

    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    const listTour = tours.value.find((tour) => tour.clientId === tourId) ?? null
    const fullTour = detailTour.value?.clientId === tourId ? detailTour.value : null

    if (!listTour && !fullTour) {
      return
    }

    // The personal "My Activities & Routes" list (listTour) only ever contains tours the
    // current user created, so canEdit is only meaningful — and only available — on fullTour.
    if (
      fullTour &&
      !(await requireItemEditable(fullTour.canEdit, 'activity', fullTour.createdByName))
    )
      return

    workspaceStore._createEventPushed = true

    const currentName = fullTour?.name ?? listTour?.name
    const currentDescription = fullTour?.description ?? listTour?.description ?? ''
    const currentType = fullTour?.type ?? listTour?.type ?? undefined
    const currentRoutingProfile = fullTour?.routingProfile ?? listTour?.routingProfile
    const changes: Array<{ field: string; value: string | null }> = []

    if (updates.name !== undefined && currentName !== updates.name) {
      changes.push({ field: 'name', value: updates.name })
    }

    if (updates.description !== undefined && currentDescription !== updates.description) {
      changes.push({ field: 'description', value: updates.description })
    }

    if (updates.type !== undefined && currentType !== updates.type) {
      changes.push({ field: 'type', value: updates.type ?? null })
    }

    if (updates.routingProfile !== undefined && currentRoutingProfile !== updates.routingProfile) {
      changes.push({ field: 'routingProfile', value: updates.routingProfile })
    }

    if (changes.length > 0) {
      await eventStore.addEvent({
        type: EventType.TOUR_UPDATED,
        payload: {
          clientId: tourId,
          changes,
        },
      })
      markTourLocallyModified(tourId)
    }

    if (listTour) {
      if (updates.name !== undefined) {
        listTour.name = truncateToMaxLength(updates.name, 255, 'Tour name') || updates.name
      }
      if (updates.description !== undefined) {
        listTour.description =
          truncateToMaxLength(updates.description, 2048, 'Tour description') || updates.description
      }
      if (updates.type !== undefined) {
        listTour.type = updates.type
      }
      if (updates.routingProfile !== undefined) {
        listTour.routingProfile = updates.routingProfile
      }
    }

    if (fullTour) {
      if (updates.name !== undefined) {
        fullTour.name = truncateToMaxLength(updates.name, 255, 'Tour name') || updates.name
      }
      if (updates.description !== undefined) {
        fullTour.description =
          truncateToMaxLength(updates.description, 2048, 'Tour description') || updates.description
      }
      if (updates.type !== undefined) {
        fullTour.type = updates.type
      }
      if (updates.routingProfile !== undefined) {
        fullTour.routingProfile = updates.routingProfile ?? undefined
      }
    }
  }

  const deleteTour = async (tourId: string) => {
    throwIfOfflineReadOnly('delete tour')

    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    // Callers pass only the tourId; the richer Tour (with canEdit) may live in the open detail
    // view or in the active collection's tour list — check both before falling back to
    // "unknown, allow" (personal-library-only tours have no canEdit to check: the /tours/ list
    // is always the current user's own).
    const fullTour =
      (detailTour.value?.clientId === tourId ? detailTour.value : null) ??
      workspaceStore.tours.find((tour) => tour.clientId === tourId) ??
      null
    if (
      fullTour &&
      !(await requireItemEditable(fullTour.canEdit, 'activity', fullTour.createdByName))
    )
      return

    workspaceStore._createEventPushed = true

    await eventStore.addEvent({
      type: EventType.TOUR_DELETED,
      payload: { clientId: tourId },
    })

    removeTours([tourId])
  }

  const reset = () => {
    listLoadGeneration += 1
    detailLoadGeneration += 1
    tours.value = []
    totalCount.value = 0
    searchQuery.value = ''
    showNoWorkspace.value = false
    dateFrom.value = null
    dateTo.value = null
    isLoading.value = false
    hasLoaded.value = false
    hoveredTourId.value = null
    focusedTourId.value = null
    scrollTargetTourId.value = null
    isSelectionMode.value = false
    recentlyUploadedIds.value = new Set()
    uploadedBatchIds.value = new Set()
    showOnlyUploadedBatch.value = false
    selectedTourIds.value = new Set()
    locallyModifiedTourIds.value = new Set()
    pinnedTourIds.value = new Set()
    clientSideFilteredIds.value = null
    uploadedTourDrafts.clear()
    detailTour.value = null
    isLoadingDetail.value = false
  }

  /**
   * Create a new empty tour with a timestamp-based name.
   * Returns the tour's clientId so callers can immediately open/select it.
   */
  const createEmptyTour = async (input?: { type?: string }): Promise<string | null> => {
    throwIfOfflineReadOnly('create tour')

    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()

    const tourId = generateId()
    const now = new Date()
    const pad = (n: number) => String(n).padStart(2, '0')
    const timestamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(now.getSeconds())}`
    const tourName = `${timestamp} - New Tour`

    const tour: Tour = {
      clientId: tourId,
      name: tourName,
      description: '',
      type: input?.type,
      tracks: [],
      waypoints: [],
      routePoints: [],
      visible: true,
      active: false,
      order: 0,
      distance: null,
      time_moving: null,
      time_elapsed: null,
      sourceFilename: null,
    }

    const eventPayload = {
      workspaceClientId: null,
      ...tour,
      eventFileName: 'generated',
      eventFileOriginalName: 'generated',
      eventFileMimeType: 'application/json',
      eventFileCompressed: false,
    }

    workspaceStore._createEventPushed = true
    await eventStore.addEvent({
      type: EventType.TOUR_CREATED,
      payload: eventPayload,
    } as Omit<Event, 'clientId' | 'timestamp'>)

    const tourListItem: TourListItem = {
      clientId: tourId,
      name: tourName,
      description: '',
      type: input?.type ?? null,
      distance: null,
      elevationGain: null,
      startDate: null,
      endDate: null,
      createdAt: now.toISOString(),
      workspaces: [],
      tracks: [],
    }

    // Prepend the new tour. Update the batch IDs so 'Recently added' can filter
    // to it later, but do NOT activate the filter automatically. Accumulate the
    // tour into pinnedTourIds so it stays visible in list + map regardless of
    // current filters until the user explicitly changes a filter.
    tours.value = [tourListItem, ...tours.value]
    totalCount.value += 1
    selectedTourIds.value = new Set([...selectedTourIds.value, tourId])
    recentlyUploadedIds.value = new Set([tourId])
    uploadedBatchIds.value = new Set([...uploadedBatchIds.value, tourId])
    pinnedTourIds.value = new Set([...pinnedTourIds.value, tourId])
    markTourLocallyModified(tourId)
    scheduleFetchCreatedAt(tourId)

    // Eagerly expose the new tour as `detailTour` so library UI can show and
    // edit the new draft immediately without waiting for a separate detail load.
    detailTour.value = tour

    logService.info(LOG_CATEGORIES.UI, 'Created empty tour', { tourId, tourName })

    return tourId
  }

  const formatRecordedTourName = (timestamp: number): string => {
    const date = new Date(timestamp)
    const pad = (value: number) => String(value).padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())} - Recorded Tour`
  }

  const recordingPointToWaypoint = (point: RecordingPoint): Waypoint => {
    const gpsAccuracy =
      point.accuracy != null && Number.isFinite(point.accuracy)
        ? Math.round(point.accuracy)
        : undefined

    return {
      clientId: generateId(),
      lat: point.lat,
      lon: point.lon,
      ele: point.ele ?? undefined,
      time: point.time,
      gps_accuracy: gpsAccuracy,
      speed: point.speed ?? undefined,
    }
  }

  const buildRecordingSegments = (draft: RecordingDraft): Segment[] => {
    const groupedPoints = draft.points
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lon))
      .sort((a, b) => a.time - b.time)
      .reduce((groups, point) => {
        const segmentIndex = Number.isFinite(point.segmentIndex) ? point.segmentIndex : 0
        const group = groups.get(segmentIndex) ?? []
        group.push(recordingPointToWaypoint(point))
        groups.set(segmentIndex, group)
        return groups
      }, new Map<number, Waypoint[]>())

    return Array.from(groupedPoints.entries())
      .sort(([leftIndex], [rightIndex]) => leftIndex - rightIndex)
      .map(([, points]) => simplifyTrackPoints(points, 5))
      .filter((points) => points.length >= 2)
      .map((points) => ({
        clientId: generateId(),
        points,
      }))
  }

  const createTourFromRecordingDraft = async (draft: RecordingDraft): Promise<string | null> => {
    const segments = buildRecordingSegments(draft)
    if (segments.length === 0) {
      showError('Recording does not contain enough points to create a track.')
      return null
    }

    const eventStore = useEventStore()
    const workspaceStore = useWorkspaceStore()
    const tourId = generateId()
    const trackId = generateId()
    const allPoints = segments.flatMap((segment) => segment.points)
    const startTime = allPoints[0]?.time ?? draft.startedAt
    const endTime = allPoints[allPoints.length - 1]?.time ?? draft.endedAt ?? startTime
    const color = TrackColorService.getColorByIndex(tours.value.length)
    const tourName = formatRecordedTourName(startTime)

    const track: Track = {
      clientId: trackId,
      name: 'Recorded Track',
      segments,
      visible: true,
      color,
      startTime,
      endTime,
    }
    refreshTrackMetrics(track)

    const tour: Tour = {
      clientId: tourId,
      name: tourName,
      description: '',
      type: undefined,
      tracks: [track],
      waypoints: [],
      routePoints: [],
      visible: true,
      active: false,
      order: 0,
      distance: null,
      elevationGain: null,
      time_moving: null,
      time_elapsed: null,
      sourceFilename: 'recording.json',
    }
    refreshTourTrackStats(tour)

    const eventPayload = {
      workspaceClientId: null,
      ...tour,
      eventFileName: 'recording.json',
      eventFileOriginalName: 'recording.json',
      eventFileMimeType: 'application/json',
      eventFileCompressed: false,
    }

    workspaceStore._createEventPushed = true
    await eventStore.addEvent({
      type: EventType.TOUR_CREATED,
      payload: eventPayload,
    } as Omit<Event, 'clientId' | 'timestamp'>)

    const tourListItem: TourListItem = {
      clientId: tourId,
      name: tourName,
      description: '',
      type: null,
      distance: tour.distance,
      elevationGain: tour.elevationGain ?? null,
      startDate: startTime ? new Date(startTime).toISOString() : null,
      endDate: endTime ? new Date(endTime).toISOString() : null,
      workspaces: [],
      tracks: [
        {
          clientId: trackId,
          pointsLineReduced500m: buildTrackLineString(track.segments),
          color,
        },
      ],
    }

    prependTours([tourListItem])
    markTourLocallyModified(tourId)
    uploadedTourDrafts.set(tourId, tour)
    detailTour.value = tour
    setFocusedTour(tourId)
    window.dispatchEvent(new CustomEvent('library-tours-uploaded'))

    logService.info(LOG_CATEGORIES.UI, 'Created tour from recording draft', {
      tourId,
      trackId,
      pointCount: draft.pointCount,
      segmentCount: segments.length,
    })

    return tourId
  }

  const addWaypoint = async (
    tourId: string,
    waypointInput: {
      lat: number
      lon: number
      ele?: number | null
      name?: string
      description?: string
      time?: number
      type?: string
    },
  ): Promise<string | null> => {
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return null
    if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return null

    const eventStore = useEventStore()
    const clientId = generateId()
    const waypoint: Waypoint = {
      clientId,
      lat: waypointInput.lat,
      lon: waypointInput.lon,
      ele: waypointInput.ele ?? undefined,
      name: truncateToMaxLength(waypointInput.name || '', 255, 'Waypoint name') || undefined,
      description:
        truncateToMaxLength(waypointInput.description || '', 2048, 'Waypoint description') ||
        undefined,
      time: waypointInput.time ?? Date.now(),
      type: waypointInput.type || '',
      _previousState: undefined,
    }

    await eventStore.addEvent({
      type: EventType.WAYPOINT_CREATED,
      payload: {
        tourClientId: tourId,
        ...waypoint,
      },
    })

    tour.waypoints.push(waypoint)
    markTourLocallyModified(tourId)
    return clientId
  }

  const deleteWaypoint = async (tourId: string, waypointIndex: number) => {
    const eventStore = useEventStore()
    const uiStore = useUiStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null

    if (tour && waypointIndex >= 0 && waypointIndex < tour.waypoints.length) {
      // Tour waypoints follow the parent tour's creator-only edit rule (validate_tour_access).
      if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return

      const waypoint = tour.waypoints[waypointIndex]

      const waypointId = `${tourId}_${waypointIndex}`
      if (uiStore.hoveredWaypointId === waypointId) {
        uiStore.setHoveredWaypoint(null)
      }

      const removedWaypoint = tour.waypoints.splice(waypointIndex, 1)[0]

      try {
        await eventStore.addEvent({
          type: EventType.WAYPOINT_DELETED,
          payload: {
            clientId: waypoint.clientId,
          },
        })
      } catch (error) {
        tour.waypoints.splice(waypointIndex, 0, removedWaypoint)
        showError(
          `Failed to delete waypoint: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }

      markTourLocallyModified(tourId)
    }
  }

  const addRoutePointToTour = async (
    tourId: string,
    routePointInput: {
      clientId?: string
      lat: number
      lon: number
      ele?: number
      name?: string
      description?: string
      order?: number
      trackClientId?: string
    },
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return null
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return null
    const clientId = routePointInput.clientId ?? generateId()
    const routePoint: RoutePoint = {
      clientId,
      lat: routePointInput.lat,
      lon: routePointInput.lon,
      ele: routePointInput.ele,
      name: routePointInput.name
        ? truncateToMaxLength(routePointInput.name, 255, 'Route point name')
        : undefined,
      description: routePointInput.description
        ? truncateToMaxLength(routePointInput.description, 2048, 'Route point description')
        : undefined,
      trackClientId: requireTrackClientId(routePointInput.trackClientId, 'New route point'),
      order:
        routePointInput.order ??
        getSparseTrackAppendOrder(
          detailTour.value?.clientId === tourId ? detailTour.value.routePoints : [],
          routePointInput.trackClientId,
        ),
    }

    await eventStore.addEvent({
      type: EventType.ROUTE_POINT_CREATED,
      payload: routePoint,
    })

    if (detailTour.value?.clientId === tourId) {
      detailTour.value.routePoints.push(routePoint)
    }

    markTourLocallyModified(tourId)
    return clientId
  }

  const insertRoutePointAt = async (
    tourId: string,
    insertIndex: number,
    routePointInput: {
      clientId?: string
      lat: number
      lon: number
      ele?: number
      name?: string
      description?: string
      trackClientId?: string
    },
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return null
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return null
    if (insertIndex < 0 || insertIndex > tour.routePoints.length) return null

    const trackClientId = requireTrackClientId(
      routePointInput.trackClientId ??
        (insertIndex > 0 ? tour.routePoints[insertIndex - 1].trackClientId : undefined) ??
        (insertIndex < tour.routePoints.length
          ? tour.routePoints[insertIndex].trackClientId
          : undefined),
      'Inserted route point',
    )

    const insertPlan = getRoutePointInsertOrderPlan(tour.routePoints, insertIndex, trackClientId)
    if (!insertPlan) return null

    const clientId = routePointInput.clientId ?? generateId()
    const routePoint: RoutePoint = {
      clientId,
      lat: routePointInput.lat,
      lon: routePointInput.lon,
      ele: routePointInput.ele,
      name: routePointInput.name
        ? truncateToMaxLength(routePointInput.name, 255, 'Route point name')
        : undefined,
      description: routePointInput.description
        ? truncateToMaxLength(routePointInput.description, 2048, 'Route point description')
        : undefined,
      order: insertPlan.insertOrder,
      trackClientId,
    }

    tour.routePoints.splice(insertIndex, 0, routePoint)
    insertPlan.shifts.forEach(({ routePoint: existing, newOrder }) => {
      existing.order = newOrder
    })

    await eventStore.runInSyncBatch(async () => {
      for (const { routePoint: existing, newOrder } of insertPlan.shifts) {
        await eventStore.queueRoutePointUpdate(existing.clientId, [
          { field: 'order', value: newOrder },
        ])
      }

      await eventStore.addEvent({
        type: EventType.ROUTE_POINT_CREATED,
        payload: routePoint,
      })
    })
    markTourLocallyModified(tourId)
    return clientId
  }

  const insertRoutePointsAt = async (
    tourId: string,
    insertIndex: number,
    routePointInputs: Array<{
      clientId?: string
      lat: number
      lon: number
      ele?: number
      name?: string
      description?: string
      trackClientId?: string
    }>,
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return []
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return []
    if (insertIndex < 0 || insertIndex > tour.routePoints.length) return []
    if (routePointInputs.length === 0) return []

    const trackClientId = requireTrackClientId(
      routePointInputs[0]?.trackClientId ??
        (insertIndex > 0 ? tour.routePoints[insertIndex - 1].trackClientId : undefined) ??
        (insertIndex < tour.routePoints.length
          ? tour.routePoints[insertIndex].trackClientId
          : undefined),
      'Inserted route points',
    )

    const insertPlan = getRoutePointMultiInsertOrderPlan(
      tour.routePoints,
      insertIndex,
      routePointInputs.length,
      trackClientId,
    )
    if (!insertPlan) return []

    const routePoints = routePointInputs.map((routePointInput, index) => ({
      clientId: routePointInput.clientId ?? generateId(),
      lat: routePointInput.lat,
      lon: routePointInput.lon,
      ele: routePointInput.ele,
      name: routePointInput.name
        ? truncateToMaxLength(routePointInput.name, 255, 'Route point name')
        : undefined,
      description: routePointInput.description
        ? truncateToMaxLength(routePointInput.description, 2048, 'Route point description')
        : undefined,
      order: insertPlan.insertOrders[index] ?? insertPlan.insertOrderStart + index,
      trackClientId,
    }))

    tour.routePoints.splice(insertIndex, 0, ...routePoints)
    insertPlan.shifts.forEach(({ routePoint: existing, newOrder }) => {
      existing.order = newOrder
    })

    await eventStore.runInSyncBatch(async () => {
      for (const { routePoint: existing, newOrder } of insertPlan.shifts) {
        await eventStore.queueRoutePointUpdate(existing.clientId, [
          { field: 'order', value: newOrder },
        ])
      }

      for (const routePoint of routePoints) {
        await eventStore.addEvent({
          type: EventType.ROUTE_POINT_CREATED,
          payload: routePoint,
        })
      }
    })
    markTourLocallyModified(tourId)
    return routePoints.map((routePoint) => routePoint.clientId)
  }

  const deleteRoutePoint = async (tourId: string, routePointIndex: number) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null

    if (tour && routePointIndex >= 0 && routePointIndex < tour.routePoints.length) {
      if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return
      const routePoint = tour.routePoints[routePointIndex]
      const removedRoutePoint = tour.routePoints.splice(routePointIndex, 1)[0]

      try {
        await eventStore.runInSyncBatch(async () => {
          await eventStore.queueRoutePointDelete(routePoint.clientId)
        })
      } catch (error) {
        tour.routePoints.splice(routePointIndex, 0, removedRoutePoint)
        showError(
          `Failed to delete route point: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }

      markTourLocallyModified(tourId)
    }
  }

  const deleteRoutePoints = async (tourId: string, routePointIndices: number[]) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return 0
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return 0

    const { removedRoutePoints } = getRoutePointMultiDeletePlan(tour.routePoints, routePointIndices)
    if (removedRoutePoints.length === 0) return 0

    const originalRoutePoints = [...tour.routePoints]
    const previousOrders = new Map(
      originalRoutePoints.map((routePoint) => [routePoint.clientId, routePoint.order]),
    )
    const removedRoutePointIds = new Set(
      removedRoutePoints.map((routePoint) => routePoint.clientId),
    )

    tour.routePoints = originalRoutePoints.filter(
      (routePoint) => !removedRoutePointIds.has(routePoint.clientId),
    )

    try {
      await eventStore.runInSyncBatch(async () => {
        for (const routePoint of removedRoutePoints) {
          await eventStore.queueRoutePointDelete(routePoint.clientId)
        }
      })
    } catch (error) {
      originalRoutePoints.forEach((routePoint) => {
        const previousOrder = previousOrders.get(routePoint.clientId)
        if (previousOrder !== undefined) {
          routePoint.order = previousOrder
        }
      })
      tour.routePoints = originalRoutePoints
      showError(
        `Failed to delete route points: ${error instanceof Error ? error.message : String(error)}`,
      )
      throw error
    }

    markTourLocallyModified(tourId)
    return removedRoutePoints.length
  }

  const reorderTrackRoutePoints = async (
    tourId: string,
    trackClientId: string,
    orderedRoutePointClientIds: string[],
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

    const originalRoutePoints = [...tour.routePoints]
    const trackRoutePoints = originalRoutePoints.filter(
      (routePoint) => (routePoint.trackClientId ?? null) === trackClientId,
    )
    if (trackRoutePoints.length === 0) return
    if (orderedRoutePointClientIds.length !== trackRoutePoints.length) {
      throw new Error('Failed to reorder route points: route point count mismatch')
    }

    const previousOrders = new Map(
      trackRoutePoints.map((routePoint) => [routePoint.clientId, routePoint.order] as const),
    )
    const reorderPlan = getSparseTrackReorderPlan(
      tour.routePoints,
      trackClientId,
      orderedRoutePointClientIds,
    )
    const reorderedTrackRoutePoints = reorderPlan.reorderedRoutePoints
    reorderPlan.changedRoutePoints.forEach(({ routePoint, newOrder }) => {
      routePoint.order = newOrder
    })

    let reorderedTrackIndex = 0
    tour.routePoints = originalRoutePoints.map((routePoint) =>
      (routePoint.trackClientId ?? null) === trackClientId
        ? reorderedTrackRoutePoints[reorderedTrackIndex++]
        : routePoint,
    )

    try {
      await eventStore.runInSyncBatch(async () => {
        const routePointOrderUpdates = reorderPlan.changedRoutePoints
          .map<RoutePointOrderEmission | null>(({ routePoint, newOrder }) => {
            const previousOrder = previousOrders.get(routePoint.clientId)
            if (previousOrder === undefined) return null
            return {
              clientId: routePoint.clientId,
              previousOrder,
              nextOrder: newOrder,
              finalChanges: [{ field: 'order', value: newOrder }],
            }
          })
          .filter((update): update is RoutePointOrderEmission => update !== null)

        await emitRoutePointOrderChanges(
          (clientId, changes) => eventStore.queueRoutePointUpdate(clientId, changes),
          routePointOrderUpdates,
          trackRoutePoints.map((routePoint) => previousOrders.get(routePoint.clientId) ?? -1),
          reorderPlan.changedRoutePoints.map(({ newOrder }) => newOrder),
        )
      })
    } catch (error) {
      trackRoutePoints.forEach((routePoint) => {
        const previousOrder = previousOrders.get(routePoint.clientId)
        if (previousOrder !== undefined) {
          routePoint.order = previousOrder
        }
      })
      tour.routePoints = originalRoutePoints
      showError(
        `Failed to reorder route points: ${error instanceof Error ? error.message : String(error)}`,
      )
      throw error
    }

    markTourLocallyModified(tourId)
  }

  const replaceSegments = async (
    tourId: string,
    trackClientId: string,
    deletedIndices: number[],
    newSegments: { segment: Segment; order: number }[],
    options?: { skipLineStringRebuild?: boolean },
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

    const track = tour.tracks.find((t) => t.clientId === trackClientId)
    if (!track) return

    const validDeletedIndices = deletedIndices.filter((i) => i >= 0 && i < track.segments.length)

    // Detect 1:1 in-place replacement → emit SEGMENT_UPDATED preserving identity
    const sortedDeleted = [...validDeletedIndices].sort((a, b) => a - b)
    const sortedNew = [...newSegments].sort((a, b) => a.order - b.order)
    const isInPlace =
      sortedDeleted.length > 0 &&
      sortedDeleted.length === sortedNew.length &&
      sortedDeleted.every((idx, i) => idx === sortedNew[i].order)

    if (isInPlace) {
      const updates: Array<
        {
          clientId: string
          trackClientId: string
          order: number
          points: Segment['points']
        } & Partial<Omit<Segment, 'clientId' | 'points'>>
      > = []
      for (let i = 0; i < sortedDeleted.length; i++) {
        const oldSeg = track.segments[sortedDeleted[i]]
        const preservedClientId = oldSeg.clientId!
        const newSeg = sortedNew[i].segment
        newSeg.clientId = preservedClientId
        track.segments[sortedDeleted[i]] = newSeg
        updates.push({
          ...newSeg,
          clientId: preservedClientId,
          trackClientId,
          order: sortedNew[i].order,
          points: newSeg.points,
        })
      }

      if (!options?.skipLineStringRebuild) {
        const updatedLineString = buildLineStringFromSegments(track.segments)
        track.pointsLineReduced5m = updatedLineString

        const tourListItem = tours.value.find((t) => t.clientId === tourId)
        if (tourListItem) {
          const listTrack = tourListItem.tracks.find((t) => t.clientId === trackClientId)
          if (listTrack) {
            listTrack.pointsLineReduced500m = updatedLineString
          }
        }
      }
      refreshTrackMetrics(track)
      refreshTourTrackStats(tour)
      syncTourListMetrics(tourId, tour)

      await eventStore.runInSyncBatch(async () => {
        for (const update of updates) {
          await eventStore.addEvent({
            type: EventType.SEGMENT_UPDATED,
            payload: update,
          })
        }
      })

      markTourLocallyModified(tourId)
      return
    }

    // Fallback: DELETE + CREATE (segment count changes)
    const toDelete = [...validDeletedIndices].sort((a, b) => b - a)

    const deletedClientIds: string[] = []
    for (const idx of toDelete) {
      const seg = track.segments[idx]
      if (seg.clientId) deletedClientIds.push(seg.clientId)
    }

    // Capture original array positions of surviving segments so we can
    // realign their backend `order` to the new array index after splicing.
    // Without this, inserting new segments mid-array leaves existing
    // segments with stale `order` values that collide with the newly
    // created ones on the server → non-deterministic order on reload.
    const toDeleteSet = new Set(toDelete)
    const survivorOriginalOrder = new Map<string, number>()
    track.segments.forEach((seg, i) => {
      if (seg.clientId && !toDeleteSet.has(i)) {
        survivorOriginalOrder.set(seg.clientId, i)
      }
    })

    for (const idx of toDelete) {
      track.segments.splice(idx, 1)
    }

    for (const { segment, order } of sortedNew) {
      const insertAt = Math.min(order, track.segments.length)
      track.segments.splice(insertAt, 0, segment)
    }

    if (!options?.skipLineStringRebuild) {
      const updatedLineString = buildLineStringFromSegments(track.segments)
      track.pointsLineReduced5m = updatedLineString

      const tourListItem = tours.value.find((t) => t.clientId === tourId)
      if (tourListItem) {
        const listTrack = tourListItem.tracks.find((t) => t.clientId === trackClientId)
        if (listTrack) {
          listTrack.pointsLineReduced500m = updatedLineString
        }
      }
    }
    refreshTrackMetrics(track)
    refreshTourTrackStats(tour)
    syncTourListMetrics(tourId, tour)

    const createdClientIds = new Set(
      sortedNew.map((n) => n.segment.clientId).filter((id): id is string => !!id),
    )
    const orderShifts: { clientId: string; newOrder: number }[] = []
    track.segments.forEach((seg, newIdx) => {
      if (!seg.clientId) return
      if (createdClientIds.has(seg.clientId)) return
      const origIdx = survivorOriginalOrder.get(seg.clientId)
      if (origIdx !== undefined && origIdx !== newIdx) {
        orderShifts.push({ clientId: seg.clientId, newOrder: newIdx })
      }
    })
    const orderedOrderShifts = sortSegmentOrderShiftsForEmission(orderShifts, survivorOriginalOrder)

    await eventStore.runInSyncBatch(async () => {
      for (const clientId of deletedClientIds) {
        await eventStore.addEvent({
          type: EventType.SEGMENT_DELETED,
          payload: { clientId },
        })
      }
      for (const { clientId, newOrder } of orderedOrderShifts) {
        await eventStore.addEvent({
          type: EventType.SEGMENT_UPDATED,
          payload: {
            clientId,
            trackClientId,
            order: newOrder,
          },
        })
      }
      for (const { segment, order } of sortedNew) {
        await eventStore.addEvent({
          type: EventType.SEGMENT_CREATED,
          payload: {
            ...segment,
            trackClientId,
            clientId: segment.clientId!,
            order,
            points: segment.points,
          },
        })
      }
    })

    markTourLocallyModified(tourId)
  }

  const replaceAllSegments = async (
    tourId: string,
    trackClientId: string,
    oldSegmentClientIds: string[],
    newSegments: Segment[],
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

    const track = tour.tracks.find((t) => t.clientId === trackClientId)
    if (!track) return

    await eventStore.runInSyncBatch(async () => {
      // Delete old segments on backend
      for (const clientId of oldSegmentClientIds) {
        await eventStore.addEvent({
          type: EventType.SEGMENT_DELETED,
          payload: { clientId },
        })
      }

      // Create new segments on backend
      for (let i = 0; i < newSegments.length; i++) {
        const segment = newSegments[i]
        await eventStore.addEvent({
          type: EventType.SEGMENT_CREATED,
          payload: {
            ...segment,
            trackClientId,
            clientId: segment.clientId!,
            order: i,
            points: segment.points,
          },
        })
      }
    })

    // Update local state
    track.segments = newSegments
    track.pointsLineReduced5m = buildLineStringFromSegments(newSegments)
    refreshTrackMetrics(track)
    refreshTourTrackStats(tour)

    // Also update tour list item
    const tourListItem = tours.value.find((t) => t.clientId === tourId)
    if (tourListItem) {
      const listTrack = tourListItem.tracks.find((t) => t.clientId === trackClientId)
      if (listTrack) {
        listTrack.pointsLineReduced500m = track.pointsLineReduced5m
      }
    }
    syncTourListMetrics(tourId, tour)

    markTourLocallyModified(tourId)
  }

  const updateRoutePointPosition = async (
    tourId: string,
    rpIndex: number,
    newLat: number,
    newLon: number,
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

    const rp = tour.routePoints[rpIndex]
    if (!rp) return

    rp.lat = newLat
    rp.lon = newLon

    await eventStore.queueRoutePointUpdate(rp.clientId, [
      {
        field: 'location',
        value: `SRID=4326;POINT(${newLon} ${newLat})`,
      },
    ])

    markTourLocallyModified(tourId)
  }

  const updateWaypoint = async (
    tourId: string,
    waypointIndex: number,
    updatedWaypoint: Partial<Waypoint>,
  ) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null

    if (tour && waypointIndex >= 0 && waypointIndex < tour.waypoints.length) {
      if (!(await requireItemEditable(tour.canEdit, 'waypoint', tour.createdByName))) return

      const waypoint = tour.waypoints[waypointIndex]
      const waypointId = waypoint.clientId || generateId()

      const currentState = {
        clientId: waypoint.clientId,
        name: waypoint.name,
        description: waypoint.description,
        lat: waypoint.lat,
        lon: waypoint.lon,
        ele: waypoint.ele,
        time: waypoint.time,
        type: waypoint.type,
      }

      const newState = {
        clientId: waypoint.clientId,
        name:
          updatedWaypoint.name !== undefined
            ? truncateToMaxLength(updatedWaypoint.name, 255, 'Waypoint name')
            : waypoint.name,
        description:
          updatedWaypoint.description !== undefined
            ? truncateToMaxLength(updatedWaypoint.description, 2048, 'Waypoint description')
            : waypoint.description,
        lat: updatedWaypoint.lat !== undefined ? updatedWaypoint.lat : waypoint.lat,
        lon: updatedWaypoint.lon !== undefined ? updatedWaypoint.lon : waypoint.lon,
        ele: updatedWaypoint.ele !== undefined ? updatedWaypoint.ele : waypoint.ele,
        time: updatedWaypoint.time !== undefined ? updatedWaypoint.time : waypoint.time,
        type: updatedWaypoint.type !== undefined ? updatedWaypoint.type : waypoint.type,
      }

      if (!waypoint._previousState) {
        waypoint._previousState = { ...currentState }
      }

      const changes: { field: string; value: string | number | undefined }[] = []

      if (waypoint._previousState.name !== newState.name) {
        changes.push({ field: 'name', value: newState.name })
      }
      if (waypoint._previousState.description !== newState.description) {
        changes.push({ field: 'description', value: newState.description })
      }
      if (waypoint._previousState.lat !== newState.lat) {
        changes.push({ field: 'lat', value: newState.lat })
      }
      if (waypoint._previousState.lon !== newState.lon) {
        changes.push({ field: 'lon', value: newState.lon })
      }
      if (waypoint._previousState.ele !== newState.ele) {
        changes.push({ field: 'ele', value: newState.ele })
      }
      if (waypoint._previousState.time !== newState.time) {
        changes.push({ field: 'time', value: newState.time })
      }
      if (waypoint._previousState.type !== newState.type) {
        changes.push({ field: 'type', value: newState.type })
      }

      if (changes.length > 0) {
        await eventStore.addEvent({
          type: EventType.WAYPOINT_UPDATED,
          payload: {
            clientId: waypointId,
            changes,
          },
        })
      }

      Object.assign(waypoint, newState)
      waypoint._previousState = { ...newState }
    }
  }

  const addTrackToTour = async (
    tourId: string,
    trackInput: {
      clientId?: string
      name: string
      segments: Segment[]
      color?: string
      routingProfile?: string
      type?: string
    },
  ): Promise<string | null> => {
    const tourListItem = tours.value.find((tour) => tour.clientId === tourId)
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null

    if (!tour) return null
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return null

    const clientId = trackInput.clientId ?? generateId()
    const existingTracks =
      detailTour.value?.clientId === tourId ? detailTour.value.tracks : (tourListItem?.tracks ?? [])
    const color = trackInput.color ?? TrackColorService.getNewTrackColorForTour(existingTracks)

    const segments = ensureSegmentClientIds(trackInput.segments)

    const track: Track = {
      clientId,
      name: truncateToMaxLength(trackInput.name, 255, 'Track name'),
      segments,
      visible: true,
      color,
      routingProfile: trackInput.routingProfile,
      type: trackInput.type ?? tour?.type ?? undefined,
    }

    const eventStore = useEventStore()
    await eventStore.addEvent({
      type: EventType.TRACK_CREATED,
      payload: {
        tour_client_id: tourId,
        ...track,
      },
    })

    const pointsLineReduced500m = buildTrackLineString(track.segments)
    const trackDistance = calculateTrackDistance(track)

    if (tourListItem) {
      tourListItem.tracks.push({
        clientId,
        pointsLineReduced500m,
        color,
      })
      tourListItem.distance = (tourListItem.distance ?? 0) + trackDistance
    }

    if (detailTour.value?.clientId === tourId) {
      detailTour.value.tracks.push({
        ...track,
        pointsLineReduced5m: pointsLineReduced500m,
      })
      detailTour.value.distance = (detailTour.value.distance ?? 0) + trackDistance
    }

    markTourLocallyModified(tourId)
    return clientId
  }

  const commitTrackRouteDraft = async (input: TrackRouteDraftCommitInput): Promise<void> => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === input.tourId ? detailTour.value : null
    if (!tour) return
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

    const trackClientId = input.trackClientId
    const finalSegments = ensureSegmentClientIds(
      JSON.parse(JSON.stringify(input.draftTrack.segments)) as typeof input.draftTrack.segments,
    )
    const finalRoutePoints = (
      JSON.parse(JSON.stringify(input.draftRoutePoints)) as typeof input.draftRoutePoints
    ).map((routePoint) => ({ ...routePoint, trackClientId }))

    if (input.mode === 'drawing') {
      const existingTrackIndex = tour.tracks.findIndex((t) => t.clientId === trackClientId)

      const track: Track = {
        ...JSON.parse(JSON.stringify(input.draftTrack)),
        clientId: trackClientId,
        name: truncateToMaxLength(input.draftTrack.name ?? 'New Track', 255, 'Track name'),
        color: '#ea580c', // orange-600: drawn routes always use the selection colour
        visible: input.draftTrack.visible ?? true,
        type: input.draftTrack.type ?? tour.type ?? undefined,
        segments: finalSegments,
        pointsLineReduced5m:
          input.draftTrack.pointsLineReduced5m ?? buildLineStringFromSegments(finalSegments),
      }

      const seededRoutePoints = finalRoutePoints.map((routePoint, index) => ({
        ...routePoint,
        order: getSparseTrackSeedOrders(finalRoutePoints.length)[index] ?? routePoint.order,
      }))

      await eventStore.runInSyncBatch(async () => {
        await eventStore.addEvent({
          type: EventType.TRACK_CREATED,
          payload: {
            tour_client_id: input.tourId,
            ...track,
          },
        })
        for (const routePoint of seededRoutePoints) {
          await eventStore.addEvent({
            type: EventType.ROUTE_POINT_CREATED,
            payload: routePoint,
          })
        }
      })

      const previousTrackDistance =
        existingTrackIndex >= 0 ? calculateTrackDistance(tour.tracks[existingTrackIndex]) : 0
      if (existingTrackIndex >= 0) {
        tour.tracks.splice(existingTrackIndex, 1, track)
      } else {
        tour.tracks.push(track)
      }
      replaceTrackRoutePoints(tour, trackClientId, seededRoutePoints)

      const tourListItem = tours.value.find((t) => t.clientId === input.tourId)
      if (tourListItem) {
        const pointsLineReduced500m = buildTrackLineString(track.segments)
        const trackDistance = calculateTrackDistance(track)
        const listTrackIndex = tourListItem.tracks.findIndex((t) => t.clientId === trackClientId)
        const listTrack = { clientId: trackClientId, pointsLineReduced500m, color: track.color }
        if (listTrackIndex >= 0) {
          tourListItem.tracks.splice(listTrackIndex, 1, listTrack)
        } else {
          tourListItem.tracks.push(listTrack)
        }
        tourListItem.distance = Math.max(
          0,
          (tourListItem.distance ?? 0) - previousTrackDistance + trackDistance,
        )
      }

      refreshTrackMetrics(track)
      refreshTourTrackStats(tour)
      syncTourListMetrics(input.tourId, tour)
      markTourLocallyModified(input.tourId)
      return
    }

    const track = tour.tracks.find((candidate) => candidate.clientId === trackClientId)
    if (!track) return

    const draftRoutingProfile = input.draftTrack.routingProfile ?? null
    const baselineRoutingProfile = input.baselineTrack?.routingProfile ?? null

    const baselineSegments = clonePlain(input.baselineTrack?.segments ?? track.segments)
    const baselineRoutePoints = clonePlain(
      input.baselineRoutePoints.length > 0
        ? input.baselineRoutePoints
        : tour.routePoints.filter(
            (routePoint) => (routePoint.trackClientId ?? null) === trackClientId,
          ),
    )

    const baselineSegmentsById = new Map(
      baselineSegments
        .filter((segment) => !!segment.clientId)
        .map((segment, index) => [segment.clientId!, { segment, index }] as const),
    )
    const finalSegmentIds = new Set(
      finalSegments.map((segment) => segment.clientId).filter((id): id is string => !!id),
    )
    const deletedSegmentIds = baselineSegments
      .map((segment) => segment.clientId)
      .filter((clientId): clientId is string => !!clientId && !finalSegmentIds.has(clientId))
    const createdSegments = finalSegments
      .map((segment, order) => ({ segment, order }))
      .filter(({ segment }) => !segment.clientId || !baselineSegmentsById.has(segment.clientId))

    const survivorOriginalOrder = new Map<string, number>()
    baselineSegmentsById.forEach(({ index }, clientId) => {
      survivorOriginalOrder.set(clientId, index)
    })

    const shiftedSegmentUpdates: Array<{
      segment: Segment
      order: number
      originalOrder: number
      contentChanged: boolean
    }> = []
    const sameOrderSegmentUpdates: Array<{ segment: Segment; order: number }> = []

    finalSegments.forEach((segment, order) => {
      if (!segment.clientId) return
      const baseline = baselineSegmentsById.get(segment.clientId)
      if (!baseline) return

      const contentChanged = !sameSegment(segment, baseline.segment)
      if (baseline.index !== order) {
        shiftedSegmentUpdates.push({
          segment,
          order,
          originalOrder: baseline.index,
          contentChanged,
        })
      } else if (contentChanged) {
        sameOrderSegmentUpdates.push({ segment, order })
      }
    })

    const shiftedSegmentOrder = sortSegmentOrderShiftsForEmission(
      shiftedSegmentUpdates.map(({ segment, order }) => ({
        clientId: segment.clientId!,
        newOrder: order,
      })),
      survivorOriginalOrder,
    )
    const shiftedSegmentById = new Map(
      shiftedSegmentUpdates.map((update) => [update.segment.clientId!, update] as const),
    )

    const baselineRoutePointsById = new Map(
      baselineRoutePoints.map((routePoint) => [routePoint.clientId, routePoint] as const),
    )
    const finalRoutePointIds = new Set(finalRoutePoints.map((routePoint) => routePoint.clientId))
    const deletedRoutePointIds = baselineRoutePoints
      .filter((routePoint) => !finalRoutePointIds.has(routePoint.clientId))
      .map((routePoint) => routePoint.clientId)
    const createdRoutePoints = finalRoutePoints.filter(
      (routePoint) => !baselineRoutePointsById.has(routePoint.clientId),
    )
    const existingRoutePointUpdates = finalRoutePoints
      .map((routePoint) => {
        const baseline = baselineRoutePointsById.get(routePoint.clientId)
        if (!baseline) return null

        const changes: EventChange[] = []
        if (baseline.lat !== routePoint.lat || baseline.lon !== routePoint.lon) {
          changes.push({ field: 'location', value: routePointLocation(routePoint) })
        }
        if (baseline.order !== routePoint.order) {
          changes.push({ field: 'order', value: routePoint.order })
        }
        if ((baseline.name ?? null) !== (routePoint.name ?? null)) {
          changes.push({ field: 'name', value: routePoint.name ?? null })
        }
        if ((baseline.description ?? null) !== (routePoint.description ?? null)) {
          changes.push({ field: 'description', value: routePoint.description ?? null })
        }
        return changes.length > 0 ? { routePoint, baseline, changes } : null
      })
      .filter(
        (
          update,
        ): update is {
          routePoint: RoutePoint
          baseline: RoutePoint
          changes: EventChange[]
        } => update !== null,
      )
    const reorderedRoutePointUpdates = existingRoutePointUpdates.filter(
      ({ baseline, routePoint }) => baseline.order !== routePoint.order,
    )
    const nonOrderRoutePointUpdates = existingRoutePointUpdates.filter(
      ({ baseline, routePoint }) => baseline.order === routePoint.order,
    )

    await eventStore.runInSyncBatch(async () => {
      if (baselineRoutingProfile !== draftRoutingProfile) {
        await eventStore.addEvent({
          type: EventType.TRACK_UPDATED,
          payload: {
            clientId: trackClientId,
            changes: [{ field: 'routingProfile', value: draftRoutingProfile }],
          },
        })
      }

      for (const clientId of deletedSegmentIds) {
        await eventStore.addEvent({
          type: EventType.SEGMENT_DELETED,
          payload: { clientId },
        })
      }

      for (const { clientId, newOrder } of shiftedSegmentOrder) {
        const update = shiftedSegmentById.get(clientId)
        if (!update) continue
        await eventStore.addEvent({
          type: EventType.SEGMENT_UPDATED,
          payload: {
            ...(update.contentChanged ? update.segment : {}),
            clientId,
            trackClientId,
            order: newOrder,
            ...(update.contentChanged ? { points: clonePlain(update.segment.points) } : {}),
          },
        })
      }

      for (const { segment, order } of sameOrderSegmentUpdates) {
        await eventStore.addEvent({
          type: EventType.SEGMENT_UPDATED,
          payload: {
            ...segment,
            clientId: segment.clientId!,
            trackClientId,
            order,
            points: clonePlain(segment.points),
          },
        })
      }

      for (const { segment, order } of createdSegments) {
        const segmentClientId = segment.clientId ?? generateId()
        segment.clientId = segmentClientId
        await eventStore.addEvent({
          type: EventType.SEGMENT_CREATED,
          payload: {
            ...segment,
            trackClientId,
            clientId: segmentClientId,
            order,
            points: clonePlain(segment.points),
          },
        })
      }

      for (const clientId of deletedRoutePointIds) {
        await eventStore.queueRoutePointDelete(clientId)
      }

      for (const routePoint of createdRoutePoints) {
        await eventStore.addEvent({
          type: EventType.ROUTE_POINT_CREATED,
          payload: routePoint,
        })
      }

      await emitRoutePointOrderChanges(
        (clientId, changes) => eventStore.queueRoutePointUpdate(clientId, changes),
        reorderedRoutePointUpdates.map(({ routePoint, baseline, changes }) => ({
          clientId: routePoint.clientId,
          previousOrder: baseline.order,
          nextOrder: routePoint.order,
          finalChanges: changes,
        })),
        baselineRoutePoints.map((routePoint) => routePoint.order),
        finalRoutePoints.map((routePoint) => routePoint.order),
      )

      for (const { routePoint, changes } of nonOrderRoutePointUpdates) {
        await eventStore.queueRoutePointUpdate(routePoint.clientId, changes)
      }
    })

    track.segments = finalSegments
    track.routingProfile = input.draftTrack.routingProfile
    track.pointsLineReduced5m =
      input.draftTrack.pointsLineReduced5m ?? buildLineStringFromSegments(finalSegments)
    refreshTrackMetrics(track)
    refreshTourTrackStats(tour)
    replaceTrackRoutePoints(tour, trackClientId, finalRoutePoints)

    const tourListItem = tours.value.find((candidate) => candidate.clientId === input.tourId)
    if (tourListItem) {
      const listTrackIndex = tourListItem.tracks.findIndex(
        (candidate) => candidate.clientId === trackClientId,
      )
      const updatedListTrack = {
        clientId: trackClientId,
        pointsLineReduced500m: buildTrackLineString(track.segments),
        color: track.color,
      }
      if (listTrackIndex >= 0) {
        tourListItem.tracks.splice(listTrackIndex, 1, updatedListTrack)
      }
    }

    syncTourListMetrics(input.tourId, tour)
    markTourLocallyModified(input.tourId)
  }

  const deleteTrack = async (tourId: string, trackClientId: string) => {
    const eventStore = useEventStore()
    const tour = detailTour.value?.clientId === tourId ? detailTour.value : null
    if (!tour) return
    // Tracks follow the parent tour's creator-only edit rule (validate_tour_access).
    if (!(await requireItemEditable(tour.canEdit, 'track', tour.createdByName))) return

    const trackIndex = tour.tracks.findIndex((track) => track.clientId === trackClientId)
    if (trackIndex < 0) return

    const track = tour.tracks[trackIndex]
    const trackDistance = calculateTrackDistance(track)
    const removedTrack = tour.tracks.splice(trackIndex, 1)[0]
    const previousRoutePoints = [...tour.routePoints]
    tour.routePoints = tour.routePoints.filter(
      (routePoint) => routePoint.trackClientId !== trackClientId,
    )

    const tourListItem = tours.value.find((candidate) => candidate.clientId === tourId)
    const listTrackIndex =
      tourListItem?.tracks.findIndex((candidate) => candidate.clientId === trackClientId) ?? -1
    const removedListTrack =
      tourListItem && listTrackIndex >= 0 ? tourListItem.tracks.splice(listTrackIndex, 1)[0] : null

    if (tour.distance !== null) {
      tour.distance = Math.max(0, tour.distance - trackDistance)
    }
    if (tourListItem && tourListItem.distance !== null && tourListItem.distance !== undefined) {
      tourListItem.distance = Math.max(0, tourListItem.distance - trackDistance)
    }

    try {
      await eventStore.addEvent({
        type: EventType.TRACK_DELETED,
        payload: {
          clientId: trackClientId,
        },
      })
      useUiStore().handleTrackDeleted(tourId, trackClientId)
    } catch (error) {
      tour.tracks.splice(trackIndex, 0, removedTrack)
      tour.routePoints = previousRoutePoints
      if (tour.distance !== null) {
        tour.distance = (tour.distance ?? 0) + trackDistance
      }
      if (tourListItem && tourListItem.distance !== null && tourListItem.distance !== undefined) {
        tourListItem.distance = (tourListItem.distance ?? 0) + trackDistance
      }
      if (tourListItem && removedListTrack && listTrackIndex >= 0) {
        tourListItem.tracks.splice(listTrackIndex, 0, removedListTrack)
      }
      showError(`Failed to delete track: ${error instanceof Error ? error.message : String(error)}`)
      throw error
    }

    markTourLocallyModified(tourId)
  }

  return {
    tours,
    totalCount,
    searchQuery,
    showNoWorkspace,
    dateFrom,
    dateTo,
    isLoading,
    hasLoaded,
    hoveredTourId,
    focusedTourId,
    recentlyUploadedIds,
    uploadedBatchIds,
    showOnlyUploadedBatch,
    selectedTourIds,
    locallyModifiedTourIds,
    loadTours,
    setSearch,
    toggleNoWorkspace,
    setDateRange,
    resetFilters,
    prependTours,
    clearRecentlyUploaded,
    clearUploadedBatchFilter,
    pinnedTourIds,
    clearPinnedTours,
    setSelectedTourIds,
    toggleSelectedTour,
    ensureTourSelected,
    clearSelectedTours,
    removeTours,
    uploadTourFiles,
    createEmptyTour,
    createTourFromRecordingDraft,
    setHoveredTour,
    clearHoveredTour,
    scrollTargetTourId,
    setScrollTarget: (tourId: string) => {
      scrollTargetTourId.value = tourId
    },
    clearScrollTarget: () => {
      scrollTargetTourId.value = null
    },
    isSelectionMode,
    enterSelectionMode: () => {
      isSelectionMode.value = true
    },
    exitSelectionMode: () => {
      isSelectionMode.value = false
      clearSelectedTours()
    },
    setFocusedTour,
    clearFocusedTour,
    detailTour,
    isLoadingDetail,
    selectTourForDetail,
    loadFullTourDetail,
    updateTour,
    deleteTour,
    addTrackToTour,
    deleteTrack,
    commitTrackRouteDraft,
    addRoutePointToTour,
    insertRoutePointAt,
    insertRoutePointsAt,
    reorderTrackRoutePoints,
    addWaypoint,
    deleteWaypoint,
    deleteRoutePoints,
    deleteRoutePoint,
    replaceSegments,
    replaceAllSegments,
    updateRoutePointPosition,
    updateWaypoint,
    markTourLocallyModified,
    closeDetail,
    reset,
    clientSideFilteredIds,
    setClientFilter,
  }
})
